import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import RideForm from '../components/RideForm';
import RideCard from '../components/RideCard';
import RideCardSkeleton from '../components/RideCardSkeleton';
import LiveMap from '../components/LiveMap';
import { useLang } from '../context/LanguageContext';
import { createRide, getMyRides, cancelRide, rateRide, getOnlineVehicles, sendCompliments } from '../services/api';
import { useRideEvents } from '../hooks/useRideEvents';
import { useVehicleEvents } from '../hooks/useVehicleEvents';

import { fadeUp, stagger } from '../utils/animations';

function getGreeting(g) {
  const h = new Date().getHours();
  if (h < 12) return g.morning;
  if (h < 18) return g.afternoon;
  return g.evening;
}

export default function ClientPage({ user, onBack }) {
  const [rides, setRides]             = useState([]);
  const [loading, setLoading]         = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [page, setPage]               = useState(0);
  const [hasMore, setHasMore]         = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalRides, setTotalRides]   = useState(0);
  const [showForm, setShowForm]       = useState(false);
  const [vehicles, setVehicles]       = useState([]);
  const [loadError, setLoadError]     = useState(false);
  const { t } = useLang();
  const c = t.client;
  const prevRidesRef = useRef([]);
  const pageRef = useRef(0);

  const fetchRides = useCallback(async () => {
    try {
      const res = await getMyRides(0);
      const newRides = res.data.content;

      const prev = prevRidesRef.current;
      if (prev.length > 0) {
        newRides.forEach((ride) => {
          const prevRide = prev.find((r) => r.id === ride.id);
          if (!prevRide) return;
          if (prevRide.status === 'NEW' && ride.status === 'ACCEPTED') toast.success(c.notifyAccepted);
          else if (prevRide.status === 'ACCEPTED' && ride.status === 'ARRIVED') toast.success(c.notifyArrived);
          else if (prevRide.status === 'ARRIVED' && ride.status === 'IN_PROGRESS') toast.success(c.notifyStarted);
          else if (prevRide.status === 'IN_PROGRESS' && ride.status === 'COMPLETED') toast.success(c.notifyCompleted);
          else if (['NEW', 'ACCEPTED', 'ARRIVED', 'IN_PROGRESS'].includes(prevRide.status) && ride.status === 'CANCELLED') toast(c.notifyCancelled);
        });
      }
      prevRidesRef.current = newRides;

      setRides(newRides);
      setHasMore(!res.data.last);
      setTotalRides(res.data.totalElements);
      setPage(0);
      pageRef.current = 0;
      setLoadError(false);
    } catch (err) {
      console.error('Failed to fetch rides:', err);
      setLoadError(true);
    } finally {
      setInitialLoad(false);
    }
  }, [user.id, c]);

  useEffect(() => { fetchRides(); }, [fetchRides]);

  const refreshFromSSE = useCallback(async () => {
    try {
      const responses = await Promise.all(
        Array.from({ length: pageRef.current + 1 }, (_, i) => getMyRides(i))
      );
      const fresh = responses.flatMap((res) => res.data.content);
      const prev = prevRidesRef.current;
      if (prev.length > 0) {
        fresh.forEach((ride) => {
          const prevRide = prev.find((r) => r.id === ride.id);
          if (!prevRide) return;
          if (prevRide.status === 'NEW' && ride.status === 'ACCEPTED') toast.success(c.notifyAccepted);
          else if (prevRide.status === 'ACCEPTED' && ride.status === 'ARRIVED') toast.success(c.notifyArrived);
          else if (prevRide.status === 'ARRIVED' && ride.status === 'IN_PROGRESS') toast.success(c.notifyStarted);
          else if (prevRide.status === 'IN_PROGRESS' && ride.status === 'COMPLETED') toast.success(c.notifyCompleted);
          else if (['NEW', 'ACCEPTED', 'ARRIVED', 'IN_PROGRESS'].includes(prevRide.status) && ride.status === 'CANCELLED') toast(c.notifyCancelled);
        });
      }
      prevRidesRef.current = fresh;
      setRides(fresh);
    } catch (err) {
      console.error('Failed to refresh rides:', err);
    }
  }, [user.id, c]);

  useRideEvents(refreshFromSSE);

  useEffect(() => {
    getOnlineVehicles()
      .then((res) => setVehicles(res.data))
      .catch(() => {});
  }, []);

  useVehicleEvents(
    (data) => {
      setVehicles((prev) => {
        const next = prev.filter((v) => v.driverId !== data.driverId);
        next.push({ ...prev.find((v) => v.driverId === data.driverId), ...data });
        return next;
      });
    },
    (data) => {
      setVehicles((prev) => prev.filter((v) => v.driverId !== data.driverId));
    }
  );

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const res = await getMyRides(nextPage);
      setRides((prev) => [...prev, ...res.data.content]);
      setHasMore(!res.data.last);
      setPage(nextPage);
      pageRef.current = nextPage;
    } catch (err) {
      console.error('Failed to load more rides:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleBookRide = async (formData) => {
    setLoading(true);
    try {
      await createRide(formData);
      await fetchRides();
      setShowForm(false);
    } catch (err) {
      toast.error(c.bookError);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRide = async (rideId) => {
    try {
      await cancelRide(rideId);
      await fetchRides();
    } catch {
      toast.error(c.cancelError);
    }
  };

  const handleRateRide = async (rideId, rating, review) => {
    try {
      await rateRide(rideId, rating, review);
      await fetchRides();
    } catch {
      toast.error(c.rateError || 'Could not submit rating');
    }
  };

  const handleSendCompliments = async (rideId, compliments) => {
    try {
      await sendCompliments(rideId, compliments);
    } catch {
      toast.error(c.complimentsError || 'Could not send compliments');
    }
  };

  const active    = rides.filter((r) => r.status !== 'COMPLETED' && r.status !== 'CANCELLED');
  const completed = rides.filter((r) => r.status === 'COMPLETED');
  const cancelled = rides.filter((r) => r.status === 'CANCELLED');

  return (
    <div className="bg-slate-900 min-h-screen">
    <div className="max-w-6xl mx-auto px-6 py-10">

      {/* ── Header ── */}
      <motion.div className="mb-8" variants={fadeUp} initial="hidden" animate="show" transition={{ duration: 0.3, ease: 'easeOut' }}>
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-white/40 hover:text-white mb-3 transition-colors">{c.back}</button>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-sm text-white/40 mb-0.5">{getGreeting(t.greeting)},</p>
            <h1 className="text-2xl font-black text-white">{user.name}</h1>
            <p className="text-white/50 text-sm mt-0.5">{c.subtitle}</p>
          </div>
          {active.length === 0 ? (
            <motion.button
              onClick={() => setShowForm((v) => !v)}
              className="btn-primary self-start sm:self-auto"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
            >
              {showForm ? c.hideForm || 'Hide form' : c.newRide || 'Book new ride'}
            </motion.button>
          ) : (
            <div className="self-start sm:self-auto text-sm text-amber-400 font-medium px-4 py-2 rounded-full border border-amber-400/30 bg-amber-400/10">
              {c.hasActiveRide || 'You have an active ride'}
            </div>
          )}
        </div>
      </motion.div>

      {/* ── Stats ── */}
      <motion.div className="grid grid-cols-3 gap-2 sm:gap-4 mb-8" variants={stagger} initial="hidden" animate="show">
        {[
          { id: 'total',     value: totalRides,        label: c.totalRides, color: 'text-white' },
          { id: 'completed', value: completed.length, label: c.completed,  color: 'text-green-400' },
          { id: 'active',    value: active.length,    label: c.active,     color: 'text-primary-400' },
        ].map(({ id, value, label, color }) => (
          <motion.div key={id} variants={fadeUp} transition={{ duration: 0.25, ease: 'easeOut' }}
            className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl text-center py-3 sm:py-4 px-4">
            <div className={`text-xl sm:text-2xl font-black ${color}`}>{value}</div>
            <div className="text-xs text-white/40 mt-0.5">{label}</div>
          </motion.div>
        ))}
      </motion.div>

      {/* ── Live map ── */}
      {!initialLoad && active.length === 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-white/40 uppercase tracking-wider mb-4">{c.liveMapTitle}</h2>
          {vehicles.length > 0 ? (
            <LiveMap vehicles={vehicles} />
          ) : (
            <p className="text-white/30 text-sm">{c.liveMapEmpty}</p>
          )}
        </div>
      )}

      {/* ── Booking form (collapsible) ── */}
      <motion.div
        initial={false}
        animate={showForm
          ? { opacity: 1, height: 'auto', marginBottom: '2rem' }
          : { opacity: 0, height: 0, marginBottom: 0 }
        }
        transition={{ duration: 0.25, ease: 'easeInOut' }}
        className="overflow-hidden"
      >
        <div className="max-w-lg">
          <RideForm onSubmit={handleBookRide} loading={loading} />
        </div>
      </motion.div>

      {/* ── Skeleton ── */}
      {initialLoad && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
          {[1, 2, 3].map((i) => <RideCardSkeleton key={i} />)}
        </div>
      )}

      {/* ── Rides ── */}
      {!initialLoad && active.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-white/40 uppercase tracking-wider mb-4">{c.active} ({active.length})</h2>
          <motion.div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" variants={stagger} initial="hidden" animate="show">
            {[...active].reverse().map((r) => (
              <motion.div key={r.id} variants={fadeUp} transition={{ duration: 0.25, ease: 'easeOut' }}>
                <RideCard ride={r} showActions onCancel={handleCancelRide} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      )}

      {!initialLoad && completed.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-white/40 uppercase tracking-wider mb-4">{c.completedSection} ({completed.length})</h2>
          <motion.div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" variants={stagger} initial="hidden" animate="show">
            {[...completed].reverse().map((r) => (
              <motion.div key={r.id} variants={fadeUp} transition={{ duration: 0.25, ease: 'easeOut' }}>
                <RideCard ride={r} onRate={handleRateRide} onSendCompliments={handleSendCompliments} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      )}

      {!initialLoad && cancelled.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold text-white/40 uppercase tracking-wider mb-4">{c.cancelledSection} ({cancelled.length})</h2>
          <motion.div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" variants={stagger} initial="hidden" animate="show">
            {[...cancelled].reverse().map((r) => (
              <motion.div key={r.id} variants={fadeUp} transition={{ duration: 0.25, ease: 'easeOut' }}>
                <RideCard ride={r} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      )}

      {hasMore && !initialLoad && (
        <div className="text-center mb-8">
          <button
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="text-sm font-semibold px-6 py-2.5 rounded-full border-2 border-white/20 text-white/60 hover:border-white/40 hover:text-white transition-colors disabled:opacity-50"
          >
            {loadingMore ? '...' : c.loadMore}
          </button>
        </div>
      )}

      {!initialLoad && rides.length === 0 && !showForm && loadError && (
        <div className="flex flex-col items-center justify-center h-64 text-center border-2 border-dashed border-amber-500/30 rounded-2xl">
          <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center mb-4">
            <svg className="w-6 h-6 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
          <p className="text-white/60 font-medium">{c.loadError}</p>
          <button onClick={() => fetchRides()} className="btn-primary mt-4 text-sm">
            {c.retry}
          </button>
        </div>
      )}

      {!initialLoad && rides.length === 0 && !showForm && !loadError && (
        <div className="flex flex-col items-center justify-center h-64 text-center border-2 border-dashed border-white/20 rounded-2xl">
          <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mb-4">
            <svg className="w-6 h-6 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
            </svg>
          </div>
          <p className="text-white/60 font-medium">{c.noRides}</p>
          <p className="text-white/40 text-sm mt-1">{c.noRidesHint}</p>
          <button onClick={() => setShowForm(true)} className="btn-primary mt-4 text-sm">
            {c.newRide || 'Book your first ride'}
          </button>
        </div>
      )}
    </div>
    </div>
  );
}
